import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import { EventUnion } from 'lyearn-store/src/types/schema';

import { LinkProps } from '@/components/Link';
import routes from '@/pages/corporate/routes';
import {
  CalendarWidgetType,
  ContentEntity,
  ContentPerformanceEntity,
  ContentsWidgetType,
  ContentTodoType,
  EventsWidgetType,
  JourneyPerformanceType,
  JourneysWidgetType,
  ResourceTypeEnum,
  ReviewCycleTodoType,
  TodosWidgetType,
  TodoTypeEnum,
  WidgetTypeEnum,
} from '@/types/schema';

import { getContentUrl } from '../../../../path/utils';
import { WidgetEntityUnion } from '../components/types';

export const getEntityKey = (widgetType?: WidgetTypeEnum | null) => {
  switch (widgetType) {
    case WidgetTypeEnum.Contents:
      return 'contents';
    case WidgetTypeEnum.MyEvents:
      return 'events';
    case WidgetTypeEnum.Todos:
      return 'todos';
    case WidgetTypeEnum.CompletedLearnings:
      return 'contents';
    case WidgetTypeEnum.Journeys:
      return 'journeyPerformances';
    case WidgetTypeEnum.UpcomingEvents:
      return 'events';
    case WidgetTypeEnum.RefresherContents:
      return 'contents';
    case WidgetTypeEnum.SelfEnrolledContents:
      return 'contents';
    case WidgetTypeEnum.AssignedContents:
      return 'contents';
    case WidgetTypeEnum.Calendar:
      return 'contents';
    default:
      return 'contents';
  }
};

export const getAdaptedWidget = (widget?: any, widgetType?: WidgetTypeEnum | null) => {
  switch (widgetType) {
    case WidgetTypeEnum.Contents:
      return widget as ContentsWidgetType;
    case WidgetTypeEnum.MyEvents:
      return widget as EventsWidgetType;
    case WidgetTypeEnum.Todos:
      return widget as TodosWidgetType;
    case WidgetTypeEnum.CompletedLearnings:
      return widget as ContentsWidgetType;
    case WidgetTypeEnum.ContinueLearning:
      return widget as ContentsWidgetType;
    case WidgetTypeEnum.Journeys:
      return widget as JourneysWidgetType;
    case WidgetTypeEnum.UpcomingEvents:
      return widget as EventsWidgetType;
    case WidgetTypeEnum.RefresherContents:
      return widget as ContentsWidgetType;
    case WidgetTypeEnum.SelfEnrolledContents:
      return widget as ContentsWidgetType;
    case WidgetTypeEnum.AssignedContents:
      return widget as ContentsWidgetType;
    case WidgetTypeEnum.Calendar:
      return widget as CalendarWidgetType;
    default:
      return widget as ContentsWidgetType;
  }
};

export const getEntityTotalCount = (widget?: any, widgetType?: WidgetTypeEnum | null) => {
  switch (widgetType) {
    case WidgetTypeEnum.ContinueLearning:
    case WidgetTypeEnum.CompletedLearnings:
    case WidgetTypeEnum.RefresherContents:
    case WidgetTypeEnum.SelfEnrolledContents:
    case WidgetTypeEnum.AssignedContents:
    case WidgetTypeEnum.ContentPerformanceWidget:
    case WidgetTypeEnum.TrendingContents:
    case WidgetTypeEnum.Contents:
      return (widget as ContentsWidgetType)?.contents?.totalCount;
    case WidgetTypeEnum.UpcomingEvents:
    case WidgetTypeEnum.MyEvents:
      return (widget as EventsWidgetType)?.events?.totalCount;
    case WidgetTypeEnum.Todos:
      return (widget as TodosWidgetType)?.todos?.length;
    case WidgetTypeEnum.Journeys:
      return (widget as JourneysWidgetType)?.journeyPerformances?.totalCount;
    case WidgetTypeEnum.Calendar:
      return (widget as CalendarWidgetType)?.contents?.totalCount;
    default:
      return (widget as ContentsWidgetType)?.contents?.totalCount;
  }
};

export const resolveNestedContents = (contents: any, widgetType: WidgetTypeEnum) => {
  if (widgetType === WidgetTypeEnum.Todos) {
    return contents;
  }
  return contents?.edges?.map((edge: any) => edge.node);
};

const emptyItemFilter = (entity: any) => entity && !isEmpty(entity);
const todosFilter = (entity: any) => entity.type === TodoTypeEnum.Content; // filters out TodoTypeEnum.ReviewCycle items

export const getFilteredWidgetContent = (contents: any[], widgetType: WidgetTypeEnum) => {
  switch (widgetType) {
    case WidgetTypeEnum.Todos:
      return contents?.filter(emptyItemFilter)?.filter(todosFilter);
    default:
      return contents?.filter(emptyItemFilter);
  }
};

export const getEntityId = (entity: WidgetEntityUnion, widgetType?: WidgetTypeEnum | null) => {
  switch (widgetType) {
    case WidgetTypeEnum.Contents:
      return (entity as ContentEntity).id;
    case WidgetTypeEnum.MyEvents:
      return (entity as EventUnion).id;
    case WidgetTypeEnum.Todos:
      return entity.type === TodoTypeEnum.Content
        ? (entity as ContentTodoType).content.id
        : (entity as ReviewCycleTodoType).reviewCycle.id;
    case WidgetTypeEnum.CompletedLearnings:
      return (entity as ContentEntity).id;
    case WidgetTypeEnum.ContinueLearning:
      return (entity as ContentEntity).id;
    case WidgetTypeEnum.Journeys:
      return (entity as JourneyPerformanceType).id;
    case WidgetTypeEnum.UpcomingEvents:
      return (entity as EventUnion).id;
    case WidgetTypeEnum.RefresherContents:
      return (entity as ContentEntity).id;
    case WidgetTypeEnum.AssignedContents:
      return (entity as ContentEntity).id;
    case WidgetTypeEnum.Calendar:
      return (entity as ContentPerformanceEntity).entityId;
    default:
      return (entity as ContentEntity).id;
  }
};

export const getEntityUrl = (
  entity: WidgetEntityUnion,
  widgetType?: WidgetTypeEnum | null,
  widgetId?: string,
): LinkProps['to'] => {
  switch (widgetType) {
    case WidgetTypeEnum.Calendar:
      return getContentUrl({
        id: (entity as ContentPerformanceEntity).entityId,
        type: (entity as ContentPerformanceEntity)?.entity?.type || ResourceTypeEnum.Course,
      }).view;
    case WidgetTypeEnum.Contents:
      return {
        pathname: getContentUrl({
          id: (entity as ContentEntity).id,
          type: (entity as ContentEntity).type,
        }).view,
        query: omitBy(
          {
            // widgetId is used when linking to microlearning lesson directly
            widgetId:
              (entity as ContentEntity)?.type === ResourceTypeEnum.MicroLearning
                ? widgetId ?? (entity as ContentEntity).id
                : undefined,
          },
          isUndefined,
        ),
      };
    case WidgetTypeEnum.MyEvents:
      return routes.EventDetails((entity as EventUnion).id);
    case WidgetTypeEnum.Todos:
      return entity.type === TodoTypeEnum.Content
        ? getContentUrl({
            id: (entity as ContentTodoType).content.id,
            type: (entity as ContentTodoType).content.type,
          }).view
        : routes.SkillReview((entity as ReviewCycleTodoType).reviewCycle.id);
    case WidgetTypeEnum.CompletedLearnings:
      return getContentUrl({
        id: (entity as ContentEntity).id,
        type: (entity as ContentEntity).type,
      }).view;
    case WidgetTypeEnum.ContinueLearning:
      return getContentUrl({
        id: (entity as ContentEntity).id,
        type: (entity as ContentEntity).type,
      }).view;
    case WidgetTypeEnum.Journeys:
      return routes.JourneyConsumption((entity as JourneyPerformanceType).entityId);
    case WidgetTypeEnum.UpcomingEvents:
      return routes.EventDetails((entity as EventUnion).id);
    case WidgetTypeEnum.RefresherContents:
      return getContentUrl({
        id: (entity as ContentEntity).id,
        type: (entity as ContentEntity).type,
      }).view;
    default:
      return getContentUrl({
        id: (entity as ContentEntity).id,
        type: (entity as ContentEntity).type,
      }).view;
  }
};

export const getEntityName = (entity: WidgetEntityUnion, widgetType?: WidgetTypeEnum | null) => {
  switch (widgetType) {
    case WidgetTypeEnum.Contents:
      return (entity as ContentEntity).name;
    case WidgetTypeEnum.MyEvents:
      return (entity as EventUnion).name;
    case WidgetTypeEnum.Todos:
      return entity.type === TodoTypeEnum.Content
        ? (entity as ContentTodoType).content.name
        : (entity as ReviewCycleTodoType).reviewCycle.recurringCycleName;
    case WidgetTypeEnum.CompletedLearnings:
      return (entity as ContentEntity).name;
    case WidgetTypeEnum.ContinueLearning:
      return (entity as ContentEntity).name;
    case WidgetTypeEnum.Journeys:
      return (entity as JourneyPerformanceType)?.journey?.name;
    case WidgetTypeEnum.UpcomingEvents:
      return (entity as EventUnion).name;
    case WidgetTypeEnum.RefresherContents:
      return (entity as ContentEntity).name;
    case WidgetTypeEnum.AssignedContents:
      return (entity as ContentEntity).name;
    case WidgetTypeEnum.Calendar:
      return (entity as ContentPerformanceEntity)?.entity?.name;
    default:
      return (entity as ContentEntity).name;
  }
};

export const getEntityImage = (entity: WidgetEntityUnion, widgetType?: WidgetTypeEnum | null) => {
  switch (widgetType) {
    case WidgetTypeEnum.Contents:
      return (entity as ContentEntity)?.cover?.url;
    case WidgetTypeEnum.MyEvents:
      return (entity as EventUnion)?.cover?.url;
    case WidgetTypeEnum.Todos:
      return entity.type === TodoTypeEnum.Content
        ? (entity as ContentTodoType).content?.cover?.url
        : ''; //doesnt have cover images
    case WidgetTypeEnum.CompletedLearnings:
      return (entity as ContentEntity)?.cover?.url;
    case WidgetTypeEnum.ContinueLearning:
      return (entity as ContentEntity)?.cover?.url;
    case WidgetTypeEnum.Journeys:
      return ''; //doesnt have cover images
    case WidgetTypeEnum.UpcomingEvents:
      return (entity as EventUnion)?.cover?.url;
    case WidgetTypeEnum.RefresherContents:
      return (entity as ContentEntity)?.cover?.url;
    case WidgetTypeEnum.AssignedContents:
      return (entity as ContentEntity)?.cover?.url;
    case WidgetTypeEnum.Calendar:
      return (entity as ContentPerformanceEntity)?.entity?.cover?.url;
    default:
      return (entity as ContentEntity)?.cover?.url;
  }
};

const getContentEntityDuration = (entity: ContentEntity) => {
  if (entity.type === ResourceTypeEnum.Roleplay && (entity as any).conversationTimeLimit != null) {
    return (entity as any).conversationTimeLimit * 60;
  }
  return entity.duration;
};

export const getEntityDuration = (
  entity: WidgetEntityUnion,
  widgetType?: WidgetTypeEnum | null,
) => {
  switch (widgetType) {
    case WidgetTypeEnum.Contents:
      return getContentEntityDuration(entity as ContentEntity);
    case WidgetTypeEnum.MyEvents:
      return (entity as EventUnion).duration;
    case WidgetTypeEnum.Todos:
      return entity.type === TodoTypeEnum.Content
        ? getContentEntityDuration((entity as ContentTodoType).content as ContentEntity)
        : 0;
    case WidgetTypeEnum.CompletedLearnings:
      return getContentEntityDuration(entity as ContentEntity);
    case WidgetTypeEnum.ContinueLearning:
      return getContentEntityDuration(entity as ContentEntity);
    case WidgetTypeEnum.Journeys:
      return 0;
    case WidgetTypeEnum.UpcomingEvents:
      return (entity as EventUnion).duration;
    case WidgetTypeEnum.RefresherContents:
      return getContentEntityDuration(entity as ContentEntity);
    case WidgetTypeEnum.AssignedContents:
      return getContentEntityDuration(entity as ContentEntity);
    case WidgetTypeEnum.Calendar:
      return (entity as ContentPerformanceEntity)?.entity?.duration;
    default:
      return getContentEntityDuration(entity as ContentEntity);
  }
};

const formatStartTime = (date: Date) => {
  const day = date.getDate(); // Get day (1-31)
  const month = date.toLocaleString('en-US', { month: 'short' }); // Get short month name
  const year = date.getFullYear(); // Get full year

  const hours = date.getHours() % 12 || 12; // Convert 24-hour time to 12-hour
  const minutes = String(date.getMinutes()).padStart(2, '0'); // Pad minutes to two digits
  const period = date.getHours() >= 12 ? 'PM' : 'AM'; // Determine AM/PM

  return `${day} ${month} ${year}, ${hours}:${minutes}${period}`;
};

export const getEntityStartTime = (
  entity: WidgetEntityUnion,
  widgetType?: WidgetTypeEnum | null,
) => {
  let startTime = '';
  switch (widgetType) {
    case WidgetTypeEnum.MyEvents:
    case WidgetTypeEnum.UpcomingEvents:
      startTime = (entity as EventUnion).startTime;
      break;
    default:
      return null;
  }
  if (!startTime) {
    return null;
  }
  const date = new Date(startTime);
  const readableDateTime = formatStartTime(date);
  return readableDateTime;
};

export const getIsEventEntity = (widgetType?: WidgetTypeEnum | null) => {
  switch (widgetType) {
    case WidgetTypeEnum.MyEvents:
    case WidgetTypeEnum.UpcomingEvents:
      return true;
    default:
      return false;
  }
};
